import { useEffect, useRef } from "react";
import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementSentPackState,
} from "./types";
import { hasSentPackDrift } from "./utils";

/**
 * A refetch that fails is worth trying again; one that succeeds is not.
 *
 * Three, because the poll it rides on is a minute apart — so this spans a few
 * minutes of a flaky endpoint without hammering one that is simply down.
 */
export const SENT_PACK_DRIFT_ATTEMPTS = 3;

/**
 * Re-reads the frozen notifications payload when a send happened somewhere else.
 *
 * The payload is cached with `staleTime: Infinity` and only the approvals query
 * polls, so a resend by another manager — or in another tab — is invisible to a page
 * already open: the banner carries on offering packs that are already current, and
 * pressing it sends a second, redundant round of email. The polled endpoint reports
 * each technician's send count for exactly this, and one successful refetch converges,
 * because the payload then agrees and the comparison stops firing.
 *
 * A hook of its own so the retry rule is testable. Inline in the section it was not:
 * the first version recorded the attempt BEFORE the refetch resolved, so a single
 * failed request left the banner stale for the rest of the sitting — every later poll
 * carried the same send states, matched the recorded key, and was skipped. Now only a
 * success settles a key, a failure counts against a bounded retry, and an in-flight
 * refetch blocks a second one.
 *
 * @param refetch the notifications query's own refetch; its result decides whether
 *   this settles or retries, so it must resolve rather than throw for a failed fetch
 *   (react-query's `refetch` does).
 */
export const useSentPackDriftRefresh = ({
  recipients,
  states,
  refetch,
  pollUpdatedAt,
  enabled = true,
}: {
  recipients: PayrollStatementNotificationRecipient[];
  states?: PayrollStatementSentPackState[];
  refetch: () => Promise<{ isError?: boolean } | unknown>;
  pollUpdatedAt?: number;
  enabled?: boolean;
}) => {
  const settledRef = useRef<{ key: string; attempts: number } | null>(null);
  const inFlightRef = useRef(false);
  const drifted = enabled && hasSentPackDrift({ recipients, states });

  useEffect(() => {
    if (!drifted) {
      // Back in agreement — so a later, different drift starts from a clean slate
      // rather than inheriting a spent retry budget.
      settledRef.current = null;
      return;
    }

    const key = JSON.stringify(states ?? []);
    const previous = settledRef.current;
    const attempts = previous?.key === key ? previous.attempts : 0;
    if (inFlightRef.current || attempts >= SENT_PACK_DRIFT_ATTEMPTS) return;

    inFlightRef.current = true;
    const settle = (failed: boolean) => {
      settledRef.current = {
        key,
        // A success needs no repeat: either the payload now agrees, or the drift is
        // something a refetch cannot resolve and retrying would spin.
        attempts: failed ? attempts + 1 : SENT_PACK_DRIFT_ATTEMPTS,
      };
      inFlightRef.current = false;
    };

    Promise.resolve(refetch()).then(
      (result) =>
        settle(!!(result as { isError?: boolean } | null | undefined)?.isError),
      () => settle(true),
    );
  }, [drifted, pollUpdatedAt, refetch, states]);

  return drifted;
};
